import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { useAuthContext } from '../../contexts/auth';
import { TenantSubscription } from '../../types/subscriptions';
import { useResumeSubscription } from '../../api/subscriptions';
import { formatDate } from '../../utils';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../consts';

const DISMISSED_KEY_PREFIX = 'subscriptionBannerDismissed:';

type BannerKind =
  | 'trial_ending'
  | 'overage'
  | 'past_due'
  | 'pending_downgrade'
  | 'pending_cancellation';
type Banner = {
  kind: BannerKind;
  severity: 'warning' | 'error' | 'info';
  message: string;
  // A cancellation notice must stay put until it is acted on.
  dismissible: boolean;
};

const daysUntil = (date: string | null): number | null => {
  if (!date) return null;
  return Math.ceil((new Date(date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

const resolveBanner = (
  subscription: TenantSubscription,
  t: (key: string, options?: object) => string
): Banner | null => {
  if (subscription.is_blocked) return null;

  if (subscription.cancellation_pending) {
    return {
      kind: 'pending_cancellation',
      severity: 'warning',
      dismissible: false,
      message: t(
        'Your subscription is scheduled to end on {{date}}. You keep full access until then.',
        { date: formatDate(subscription.paid_until) }
      ),
    };
  }

  if (subscription.status === 'T') {
    const left = daysUntil(subscription.current_period_end);
    if (left !== null && left >= 0 && left <= 7) {
      return {
        kind: 'trial_ending',
        severity: subscription.needs_card ? 'warning' : 'info',
        dismissible: !subscription.needs_card,
        message: subscription.needs_card
          ? t('Your trial ends in {{count}} days. Add a payment card to keep access.', {
              count: left,
            })
          : t('Your trial ends in {{count}} days. Choose a plan to keep access.', {
              count: left,
            }),
      };
    }
  }
  if (subscription.status === 'D') {
    return {
      kind: 'past_due',
      severity: 'error',
      dismissible: true,
      message: t('We could not process your payment. Next attempt on {{date}}.', {
        date: subscription.next_retry_at ?? '',
      }),
    };
  }
  if (subscription.overage_strikes === 1) {
    return {
      kind: 'overage',
      severity: 'warning',
      dismissible: true,
      message: t('Your device count ({{count}}) exceeds the {{limit}} limit for the {{plan}} plan.', {
        count: subscription.measured_device_count ?? 0,
        limit: subscription.equipment_limit ?? '',
        plan: subscription.tariff.name,
      }),
    };
  }
  if (subscription.pending_tariff) {
    return {
      kind: 'pending_downgrade',
      severity: 'info',
      dismissible: true,
      message: t('Downgrade to {{plan}} takes effect on {{date}}.', {
        plan: subscription.pending_tariff.name,
        date: subscription.pending_tariff_effective ?? '',
      }),
    };
  }
  return null;
};

export const SubscriptionBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  const { resume, loading: resuming } = useResumeSubscription();
  const [dismissed, setDismissed] = useState<Record<string, boolean>>({});

  const subscription = currentUser?.tenant?.subscription;
  if (!subscription) return null;

  const banner = resolveBanner(subscription, t);
  if (!banner) return null;

  const dismissKey = `${DISMISSED_KEY_PREFIX}${banner.kind}`;
  if (
    banner.dismissible &&
    (dismissed[dismissKey] || sessionStorage.getItem(dismissKey) === 'true')
  ) {
    return null;
  }

  const handleAbortCancellation = async () => {
    const { success } = await resume();
    if (success) {
      window.dispatchEvent(new Event('tenantUpdated'));
      enqueueSnackbar(t('Cancellation aborted. Your subscription continues.'), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(t('Could not abort the cancellation. Please try again.'), ERROR_SNACKBAR);
    }
  };

  const actionLabel =
    banner.kind === 'pending_cancellation'
      ? t('Abort cancellation')
      : banner.kind === 'trial_ending' && subscription.needs_card
        ? t('Add card')
        : t('Manage subscription');

  const onAction = () => {
    if (banner.kind === 'pending_cancellation') {
      handleAbortCancellation();
    } else {
      navigate('/tenant/subscription');
    }
  };

  return (
    <Box>
      <Alert
        severity={banner.severity}
        onClose={
          banner.dismissible
            ? () => {
                sessionStorage.setItem(dismissKey, 'true');
                setDismissed((prev) => ({ ...prev, [dismissKey]: true }));
              }
            : undefined
        }
        action={
          <Button
            color="inherit"
            size="small"
            onClick={onAction}
            disabled={banner.kind === 'pending_cancellation' && resuming}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {actionLabel}
          </Button>
        }
      >
        {banner.message}
      </Alert>
    </Box>
  );
};
